import { useTableActionContext } from '@aibees/elephant/context';
import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { SearchModality } from '@aibees/elephant/feature/search-table-company/hooks/types';
import {
  getEmailsCleared,
  howManyEmailsFound,
  isAnyEmailNeedsReverification,
} from '@aibees/elephant/feature/search-table-people/components/people-email-popover/utils';
import {
  SaveRecordAction,
  useSearchModality,
  useSearchRouteInfo,
} from '@aibees/elephant/hooks';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import {
  ENRICHMENT_STATE,
  FeatureFlags,
  SearchTable,
  SearchType,
} from '@aibees/elephant/types';
import { CreditUsageType } from '@aibees/shared/definition';
import { useFeatureFlagsContext } from '@aibees/shared/feature-flags';
import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { NotificationPopupNoteCost } from '../../notification-popup-note-cost';
import { BasicCompanyStorageInfo } from '../components/basic-company-storage-info/basic-company-storage-info';
import * as SX from '../sx';
import { ContactOption } from '../types';
import { usePageType } from '@aibees/elephant/hooks/use-page-type';

export function useGetEnrichmentItems() {
  const { item: selectedItem } = useActionButtonContext();
  const { type, table } = useSearchRouteInfo();

  const { integrationConfig } = useTableActionContext();
  const { modality } = useSearchModality();
  const { isPeoplePage, isCompanyPage } = usePageType();

  const isCompanyEmployees = table === SearchTable.Employees;

  const isPhoneNumberEnabled = useFeatureFlagsContext().isFeatureEnabled(
    FeatureFlags.PEOPLE_PHONE_NUMBER
  );

  const title =
    type === SearchType.People
      ? 'Contact Data Enrichment:'
      : 'Account Data Enrichment:';
  const {
    aiCompany,
    aiPeople,
    aiUnsavedPeople,
    basicPeople,
    basicCompany,
    basicUnsavedPeople,
  } = useGetCreditsAmountTypeOnActionPopup();
  const isAnyEmailFound = !!howManyEmailsFound(
    getEmailsCleared(selectedItem?.revelation?.email)
  );

  const items: ContactOption[] = useMemo(
    () => [
      {
        title: 'Email Enrichment',
        value: SaveRecordAction.CONTACT_ENRICH_EMAIL,
        description: '',
        disabled: !!integrationConfig,
        icon: 'email',
        active:
          basicPeople ||
          aiPeople ||
          aiUnsavedPeople ||
          basicUnsavedPeople ||
          isPeoplePage,
        done: !!selectedItem && isAnyEmailFound,
        notificationTitle: 'Email Enrichment',
        notificationDescription: (
          <Stack spacing={1}>
            <Typography sx={SX.description}>
              Retrieves and verifies a valid email address for the selected
              contact.
            </Typography>
            <Typography sx={SX.description}>
              Charges of{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.CONTACT_EMAIL_FINDING}
              />
              credits are applied for each successful email enrichment, whether
              the contact is newly added (net new) or an existing contact being
              updated.
            </Typography>
            <Typography sx={SX.descriptionWithDifferentColor}>
              No charges apply if we cannot find a valid email address.
            </Typography>
            <Typography sx={SX.descriptionWithDifferentColor}>
              Previously saved records with already valid and enriched email
              addresses are not charged again.
            </Typography>
          </Stack>
        ),
      },
      {
        title: 'Email Re-Verify',
        value: SaveRecordAction.CONTACT_REVERIFY_EMAIL,
        description: '',
        disabled:
          modality === SearchModality.UNSAVED ||
          (!!selectedItem && !selectedItem?.revelation?.email),
        icon: 'published-with-changes',
        done:
          !!selectedItem &&
          !!selectedItem?.revelation?.email &&
          !isAnyEmailNeedsReverification(selectedItem.revelation?.email),
        active: basicPeople || aiPeople || isPeoplePage,
        notificationTitle: 'Email Re-Reverify',
        notificationDescription: (
          <Stack spacing={1}>
            <Typography sx={SX.description}>
              Re-verifies an already enriched email address to ensure its
              validity and accuracy, especially useful for maintaining
              up-to-date contact data in dynamic industries.
            </Typography>
            <Typography sx={SX.description}>
              This costs{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.CONTACT_EMAIL_REVERIFICATION}
              />{' '}
              credits per record regardless of whether the email is still valid
              or not.
            </Typography>
          </Stack>
        ),
      },
      {
        title: 'Profile Insight',
        value: SaveRecordAction.CONTACT_REVEAL_PROFILE,
        description: '',
        disabled: false,
        icon: 'badge-outline',
        active:
          basicPeople ||
          aiPeople ||
          aiUnsavedPeople ||
          basicUnsavedPeople ||
          isPeoplePage,
        done:
          !!selectedItem &&
          selectedItem.revelation?.contact?.state === ENRICHMENT_STATE.SUCCESS,
        notificationTitle: 'Profile insight',
        notificationDescription: (
          <Stack spacing={1}>
            <Typography sx={SX.description}>
              Retrieves and verifies a valid email address for the selected
              contact.
            </Typography>
            <Typography sx={SX.description}>
              Charges of{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.CONTACT_EMAIL_FINDING}
              />
              credits are applied for each successful email enrichment, whether
              the contact is newly added (net new) or an existing contact being
              updated.
            </Typography>
            <Typography sx={SX.descriptionWithDifferentColor}>
              No charges apply if we cannot find a valid email address.
            </Typography>
            <Typography sx={SX.descriptionWithDifferentColor}>
              Previously saved records with already valid and enriched email
              addresses are not charged again.
            </Typography>
          </Stack>
        ),
      },
      {
        title: 'Mobile Phone Number',
        icon: 'phone',
        disabled: !isPhoneNumberEnabled,
        value: SaveRecordAction.CONTACT_REVEAL_PHONE_CACHED,
        active:
          isPhoneNumberEnabled &&
          (basicPeople ||
            aiPeople ||
            aiUnsavedPeople ||
            basicUnsavedPeople ||
            isPeoplePage),
        notificationTitle: 'Mobile Phone Number',
        notificationDescription: (
          <Stack spacing={1}>
            <Typography sx={SX.description}>
              Retrieves and verifies a valid mobile phone number for the
              selected contact.
            </Typography>
            <Typography sx={SX.description}>
              A charge of{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.CONTACT_PHONE_FINDING}
              />{' '}
              credits is applied for each successful mobile enrichment, whether
              the contact is newly added (net new) or an existing contact with a
              new or updated mobile number.
            </Typography>
            <Typography sx={SX.description}>
              No charges apply if we cannot find a valid mobile phone number.
            </Typography>
            <Typography sx={SX.description}>
              Previously saved records with an already valid mobile phone number
              that is not updated will not be charged again.
            </Typography>
          </Stack>
        ),
      },
      {
        title: 'AI Personality Enrichment',
        value: SaveRecordAction.CONTACT_REVEAL_ANALYSIS,
        description: '',
        disabled: false,
        icon: 'person-search-outline',
        done:
          !!selectedItem &&
          selectedItem.revelation?.analysis?.state === ENRICHMENT_STATE.SUCCESS,
        active: !!selectedItem && (isPeoplePage || basicPeople),
      },
      {
        title: 'Basic Contact Storage',
        value: SaveRecordAction.BASIC_SAVE,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: basicPeople || basicUnsavedPeople || isPeoplePage,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'Basic Contact Storage',
        notificationDescription: (
          <Stack spacing={1}>
            <Typography sx={SX.description}>
              This option allows you to save, organize, label, add to lists, or
              assign ownership to contact records retrieved from a basic people
              search.
            </Typography>
            <Typography sx={SX.description}>
              Basic people searches refer to searches not powered by AI-driven
              features.
            </Typography>
            <Typography sx={SX.description}>
              Charges of{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.CONTACT_STORAGE_BASIC}
              />{' '}
              Credit are only applied for new records that have not been
              previously saved.
            </Typography>
            <Typography sx={SX.descriptionWithDifferentColor}>
              Previously saved records are free of charge.
            </Typography>
          </Stack>
        ),
      },
      // Basic Account Storage - for basic company searches
      {
        title: 'Basic Account Storage',
        value: SaveRecordAction.AI_POWERED_ACCOUNT,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: (basicCompany || isCompanyPage) && !isCompanyEmployees,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'Basic Account Storage',
        notificationDescription: <BasicCompanyStorageInfo />,
      },
      // AI-Powered Account Storage - for AI company searches
      {
        title: 'AI-Powered Account Storage',
        value: SaveRecordAction.AI_POWERED_ACCOUNT,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: aiCompany && !isCompanyEmployees,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'AI-Powered Account Storage',
        notificationDescription: (
          <Stack>
            <Typography sx={SX.description}>
              This applies to saving, exporting, adding to lists, labeling, or
              assigning owners to company records.
            </Typography>
            <Typography sx={SX.description}>
              These records are sourced from AI-driven similarity (lookalike) or
              semantic (text) searches.
            </Typography>
            <Typography sx={SX.description}>
              Credit cost are{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.CONTACT_STORAGE_AI}
              />{' '}
              per record, regardless of whether the record was previously saved
            </Typography>
          </Stack>
        ),
      },
      // AI-Powered Contact Storage - for AI people searches
      {
        title: 'AI-Powered Contact Storage',
        value: SaveRecordAction.AI_POWERED_CONTACT,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: (aiPeople || aiUnsavedPeople) && !isCompanyEmployees,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'AI-Powered Contact Storage',
        notificationDescription: (
          <Stack>
            <Typography sx={SX.description}>
              AI-Powered Contact Storage allows you to save, export, label, add
              to lists, or assign owners to contact records sourced from
              AI-driven lookalike or semantic searches.
            </Typography>
            <Typography sx={SX.description}>
              Each action{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.ACCOUNT_STORAGE_AI}
              />{' '}
              credits per record regardless of whether the contact has been
              previously saved or exported.
            </Typography>
            <Typography sx={SX.description}>
              This feature provides enriched and precise contact insights by
              leveraging advanced AI models to identify and recommend similar or
              related profiles tailored to your search criteria
            </Typography>
          </Stack>
        ),
      },
    ],
    [
      basicPeople,
      aiPeople,
      aiUnsavedPeople,
      basicUnsavedPeople,
      basicCompany,
      aiCompany,
      selectedItem,
      isAnyEmailFound,
      modality,
      isPhoneNumberEnabled,
      isPeoplePage,
      isCompanyPage,
      isCompanyEmployees,
      integrationConfig,
    ]
  );

  return { items: items.filter((i) => i.active), title };
}
